import { useQuery } from "src/hooks/search/use-query";
import { conditionalRender } from "src/utils/conditional-render";
import { SearchResultContent } from "./components/SearchResultContent";
import { NoSearchResults } from "src/molecules/NoSearchResults";
import { StyledSearchMenu } from "./SearchResults.styled";
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type Dispatch,
} from "react";
import { DefaultResults } from "./components/DefaultResults";
import { SearchResultsSkeleton } from "./components/SearchResultsSkeleton";
import { useAnimatedHeight } from "src/utils/use-animated-height";
import type { AbandonedTrackerActions } from "../Header/SubMenu";
import { getSearchResultDisplayedProps } from "./utils";
import { useSegment } from "src/hooks/useSegment";
import { SearchResultsDispatchContext } from "./search-results.context";
import { useNewIsLoggedIn } from "src/hooks/useIsLoggedIn";
import type { SuggestionType } from "src/types/adapters/query.type";
import { useBreakpoints } from "@otrium/core";
import { ShopTypeButtons } from "./components/ShopTypeButtons";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import type { CarouselType } from "src/types/Carousels";

type SearchResultsProps = {
  query: string;
  isSearchOpen: boolean;
  dispatchAbandonedTracker: Dispatch<AbandonedTrackerActions>;
  clearSearch: () => void;
  onShopTypeNavigate: () => void;
};

const SEARCH_CLICK_TYPE_MAP: Record<SuggestionType, string> = {
  brand: "brand_id_clicked",
  category: "category_id_clicked",
  collection: "collections_id_clicked",
  product: "product_id_clicked",
};

const SearchResults = forwardRef<HTMLDivElement, SearchResultsProps>(
  function SearchResults(
    { query, isSearchOpen, dispatchAbandonedTracker, onShopTypeNavigate },
    ref
  ) {
    const { isDesktop } = useBreakpoints();
    const { isLoggedIn } = useNewIsLoggedIn();
    const { ref: containerRef, style: animatedHeightStyle } =
      useAnimatedHeight<HTMLDivElement>(ref);
    const { currentShopType } = useShopTypeContext();

    const queryState = useQuery(query, {
      clearSearch: !isSearchOpen,
      searchShopType: currentShopType,
    });

    const {
      productsSearched,
      searchResultDisplayed,
      searchResultClicked,
      listViewAllSelected,
      segmentViewSearchAllBrands,
      segmentSearchRecommendationClicked,
    } = useSegment();

    const queryRef = useRef<string>();

    const displayedResults = useMemo(
      () =>
        getSearchResultDisplayedProps({
          products: queryState.results?.products || [],
          searchTerm: queryState.results?.query || "",
          suggestions: queryState.results?.suggestions || [],
          resultId: queryState.results?.resultId,
        }),
      [
        queryState.results?.products,
        queryState.results?.suggestions,
        queryState.results?.query,
        queryState.results?.resultId,
      ]
    );

    const onSuggestionClick = useCallback(
      ({
        type,
        itemId,
        position,
        gateLevel,
      }: {
        type: SuggestionType;
        itemId: string;
        position: number;
        gateLevel?: string;
      }) => {
        dispatchAbandonedTracker({
          type: "SET_USER_INTERACTED",
          payload: true,
        });

        void searchResultClicked({
          ...displayedResults,
          [SEARCH_CLICK_TYPE_MAP[type]]: itemId,
          position_clicked: position,
          gated: !isLoggedIn,
          gate_level: gateLevel,
        });
      },
      [
        dispatchAbandonedTracker,
        searchResultClicked,
        displayedResults,
        isLoggedIn,
      ]
    );

    const onSearchRecommendationClick = useCallback(
      (data: { type: CarouselType; name: string; position: number }) => {
        const { type, name, position } = data;

        dispatchAbandonedTracker({
          type: "SET_USER_INTERACTED",
          payload: true,
        });

        void segmentSearchRecommendationClicked(type, name, position);
      },
      [segmentSearchRecommendationClicked, dispatchAbandonedTracker]
    );

    const onSearchViewAllBrandsClick = useCallback(() => {
      dispatchAbandonedTracker({
        type: "SET_USER_INTERACTED",
        payload: true,
      });

      void segmentViewSearchAllBrands();
    }, [segmentViewSearchAllBrands, dispatchAbandonedTracker]);

    useEffect(() => {
      if (
        (!queryState.results?.products && !queryState.results?.suggestions) ||
        !queryState.results.query ||
        queryState.loading ||
        (queryState.results?.query &&
          queryState.results.query === queryRef.current)
      ) {
        return;
      }

      queryRef.current = queryState.results.query;

      dispatchAbandonedTracker({ type: "SET_DATA", payload: displayedResults });
      void productsSearched(queryState.results.query);
      void searchResultDisplayed(displayedResults);
    }, [
      displayedResults,
      queryState.results?.query,
      queryState.results?.suggestions,
      queryState.results?.products,
      queryState.loading,
      dispatchAbandonedTracker,
      searchResultDisplayed,
      productsSearched,
    ]);

    const onViewAllClick = useCallback(
      (position: "header" | "bottom") => {
        dispatchAbandonedTracker({
          type: "SET_USER_INTERACTED",
          payload: true,
        });

        void listViewAllSelected(
          {
            title: query,
            num_items: queryState.results?.totalProductsResult || 0,
          },
          position
        );
      },
      [
        dispatchAbandonedTracker,
        listViewAllSelected,
        query,
        queryState.results?.totalProductsResult,
      ]
    );

    const resultComponent = conditionalRender<typeof queryState>({
      currentValue: queryState,
      defaultComponent: <SearchResultsSkeleton />,
      rules: [
        {
          condition: ({ loading, results }) => !loading && !results?.query,
          component: <DefaultResults />,
        },
        {
          condition: ({ loading, results }) =>
            !loading &&
            !!results?.query &&
            !!(!results.products?.length && !results.suggestions?.length),
          component: <NoSearchResults term={query} />,
        },
        {
          condition: ({ loading, results }) =>
            !loading &&
            !!(results?.products?.length || results?.suggestions?.length),
          component: <SearchResultContent results={queryState.results} />,
        },
      ],
    });

    return (
      <StyledSearchMenu
        className={isSearchOpen ? "open" : ""}
        data-cnstrc-autosuggest
        data-cnstrc-result-id={queryState.results?.resultId}
      >
        <div
          ref={containerRef}
          style={animatedHeightStyle}
          className="search-menu-content-wrapper"
        >
          <SearchResultsDispatchContext.Provider
            value={{
              onSuggestionClick,
              onViewAllClick,
              onSearchRecommendationClick,
              onSearchViewAllBrandsClick,
              onShopTypeNavigate,
            }}
          >
            {!isDesktop ? <ShopTypeButtons /> : null}
            {resultComponent}
          </SearchResultsDispatchContext.Provider>
        </div>
      </StyledSearchMenu>
    );
  }
);

export { SearchResults };
